import React from 'react';
import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: 'website' | 'article' | 'product';
  canonical?: string;
}

const SEO: React.FC<SEOProps> = ({
  title = 'Chrono Session - AI Powered Browser Session Manager | Organize & Share Tabs',
  description = 'Organize your browser like never before! Transform tab chaos into smart, shareable sessions with AI-powered organization. Save, restore, and collaborate on browser sessions effortlessly.',
  keywords = 'browser session manager, chrome extension, tab manager, session sharing, AI tab organization, productivity tools, browser productivity, tab management, session saver, chrome tab organizer, browser workflow, smart tabs, session collaboration, browser organization, tab grouping, session analytics, browser efficiency, chrome session manager, tab productivity, browser session sharing, AI browser extension',
  image = '/og-image.png',
  url,
  type = 'website',
  canonical
}) => {
  // Auto-detect base URL for deployment flexibility
  const baseUrl = typeof window !== 'undefined' ? window.location.origin : 'https://chronosession.com';
  const fullUrl = canonical || url || baseUrl;
  const fullImageUrl = image.startsWith('http') ? image : `${baseUrl}${image}`;
  
  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content="Chrono Session" />
      {url && <link rel="canonical" href={fullUrl} />}
      
      {/* Open Graph Meta Tags */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:image" content={fullImageUrl} />
      <meta property="og:site_name" content="Chrono Session" />
      <meta property="og:locale" content="en_US" />
      
      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullImageUrl} />
      <meta name="twitter:site" content="@chronosession" />
      
      {/* Additional SEO Meta Tags */}
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta name="theme-color" content="#2563eb" />
      <meta name="msapplication-TileColor" content="#2563eb" />
      
      {/* Structured Data for Chrome Extension */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          "name": "Chrono Session",
          "description": "AI-powered browser session manager Chrome extension",
          "applicationCategory": "BrowserExtension",
          "operatingSystem": "Chrome",
          "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "USD"
          },
          "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": "4.9",
            "ratingCount": "1250"
          },
          "author": {
            "@type": "Organization",
            "name": "Chrono Session"
          }
        })}
      </script>
      
      {/* Additional Structured Data for Organization */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "Chrono Session",
          "url": baseUrl,
          "logo": `${baseUrl}/logo.png`,
          "sameAs": [
            "https://twitter.com/chronosession",
            "https://github.com/chrono-session"
          ]
        })}
      </script>
    </Helmet>
  );
};

export default SEO; 